import { Connection } from '@lucidtech/las-sdk-browser';
import { Key, memo, useCallback, useEffect, useState } from 'react';
import { ComboBoxProps } from 'react-aria-components';

import { Button, Flex, Label, ListBox, ListBoxItem, Popover, Select, SelectValue, Text } from '@/components/core';
import { ChevronIcon, ExcelTableIcon, SaveIcon } from '@/components/icons';
import { useTriggerStore } from '@/store';

import { GraphObject } from '../client';
import { useGraphClient } from '../hooks';

interface TableSelectorProps<T extends object> extends Omit<ComboBoxProps<T>, 'children'> {
  connection?: Connection;
}

const ITableSelector = <T extends object>({ connection }: TableSelectorProps<T>) => {
  const graphClient = useGraphClient(connection);
  const siteId = useTriggerStore((state) => state.action.config.siteId) as string;
  const driveId = useTriggerStore((state) => state.action.config.driveId) as string;
  const driveItemId = useTriggerStore((state) => state.action.config.driveItemId) as string;
  const currentTableId = useTriggerStore((state) => state.action.config?.tableId) as string | undefined;
  const updateAction = useTriggerStore((state) => state.updateAction);
  const [isLoading, setIsLoading] = useState(false);
  const [tables, setTables] = useState<GraphObject[]>([]);

  useEffect(() => {
    const loader = async () => {
      if (driveItemId) {
        setIsLoading(true);

        try {
          const worksheets = (await graphClient.getWorksheets(siteId, driveId, driveItemId)) ?? [];

          const tables = [];
          for (const worksheet of worksheets) {
            if (worksheet.id != null) {
              const res = await graphClient.getTables(siteId, driveId, driveItemId, worksheet.id);
              tables.push(...res);
            }
          }
          setTables(tables);
        } finally {
          setIsLoading(false);
        }
      } else {
        setTables([]);
      }
    };

    loader();
  }, [graphClient, driveItemId, siteId, driveId]);

  const onSelectionChange = useCallback(
    (key: Key | null) => {
      const tableId = key?.toString() || null;
      updateAction((state) => ({ config: { ...state.config, fieldMapping: {}, tableId } }));
    },
    [updateAction]
  );

  return (
    <Flex col className="gap-xs">
      {driveItemId && !isLoading && tables.length ? (
        <>
          <Label isRequired>Table</Label>
          <Select
            defaultSelectedKey={currentTableId}
            onSelectionChange={onSelectionChange}
            placeholder="Select table"
            isDisabled={!driveItemId}
          >
            <div className="flex grow-0">
              <Button variant="secondary">
                <Text slot="description" className="gap-sm flex grow items-center">
                  {isLoading ? <SaveIcon /> : null}
                  <ExcelTableIcon className="size-4" />
                  <SelectValue />
                </Text>
                <ChevronIcon rotation="down" />
              </Button>
            </div>
            <Popover>
              <ListBox>
                {tables.map((table) => (
                  <ListBoxItem id={table.id} key={table.id} className="py-0" textValue={table.name}>
                    <Text>{table.name}</Text>
                  </ListBoxItem>
                ))}
              </ListBox>
            </Popover>
          </Select>
        </>
      ) : (
        <></>
      )}
    </Flex>
  );
};

export const TableSelector = memo(ITableSelector) as typeof ITableSelector;
